// src/components/navigation/mobile-menu.tsx
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/components/providers/auth-provider'
import { cn, getInitials } from '@/lib/utils'
import { X, LogOut, CreditCard, Key } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { User, SubscriptionTier } from '@/types'
import { BriefingStackIcon } from '@/components/nav-icons/BriefingStackIcon'
import { DashIcon } from '@/components/nav-icons/DashIcon'
import { PreferencesIcon } from '@/components/nav-icons/PreferencesPuzzleIcon'
import { SettingsCogIcon } from '@/components/nav-icons/SettingsCogIcon'
import { VaultIcon } from '@/components/nav-icons/VaultIcon'
import type { ComponentType } from 'react'

interface NavItem {
  label: string
  href: string
  icon: LucideIcon | ComponentType<{ className?: string }>
}

interface NavDivider {
  type: 'divider'
}

type NavItemOrDivider = NavItem | NavDivider

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  isAuthenticated: boolean
  userTier: SubscriptionTier
  user: User | null
}

export function MobileMenu({
  isOpen,
  onClose,
  isAuthenticated,
  userTier,
  user
}: MobileMenuProps) {
  const { logout } = useAuth()
  const location = useLocation()

  const handleSignOut = async () => {
    try {
      await logout()
      onClose()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const navItems: NavItemOrDivider[] = [
    { label: 'Dashboard', href: '/app/dashboard', icon: DashIcon },
    { label: 'Briefings', href: '/app/briefings', icon: BriefingStackIcon },
    { label: 'Vault', href: '/app/vault', icon: VaultIcon },
    { label: 'Preferences', href: '/app/preferences', icon: PreferencesIcon },
    { label: 'Settings', href: '/app/settings', icon: SettingsCogIcon },
    { type: 'divider' },
    { label: 'Billing', href: '/app/billing', icon: CreditCard },
    { label: 'API Keys', href: '/app/api-keys', icon: Key },
  ]

  const isActive = (href: string) => location.pathname.startsWith(href)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Mobile Menu */}
      <div className="fixed inset-y-0 right-0 w-80 max-w-full bg-card border-l border-border z-50 md:hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <img
              src="/images/FULL_LOGO.svg"
              alt="Digestr Logo"
              className="h-16 auto"
            />
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          {isAuthenticated && user && (
            <div className="p-4 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-digestr-500 to-digestr-600 rounded-full flex items-center justify-center text-white font-medium">
                  {getInitials(user.full_name || user.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {user.full_name || 'User'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {user.email}
                  </p>
                  <p className="text-xs text-digestr-500 capitalize font-medium">
                    {userTier} tier
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto">
            <nav className="p-2 space-y-1">
              {navItems.map((item, idx) => {
                if ('type' in item && item.type === 'divider') {
                  return <div key={`divider-${idx}`} className="border-t border-border my-2" />
                }

                // Type guard ensures item is NavItem
                const navItem = item as NavItem
                const Icon = navItem.icon
                return (
                  <Link
                    key={navItem.href}
                    to={navItem.href}
                    onClick={onClose}
                    className={cn(
                      'flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors',
                      isActive(navItem.href)
                        ? 'bg-digestr-500/10 text-digestr-500'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{navItem.label}</span>
                  </Link>
                )
              })}

              {/* Divider */}
              <div className="border-t border-border my-2" />

              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-3 px-3 py-3 text-left hover:bg-red-500/10 rounded-lg transition-colors text-red-600 dark:text-red-400"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Sign Out</span>
              </button>
            </nav>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Digestr v1.0.0
            </p>
          </div>
        </div>
      </div>
    </>
  )
}